import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Alert,
} from '@mui/material';
import BrandMark from '../components/BrandMark';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice';
import { individualLogin } from '../services/authService';
import { isTwoFactorChallenge } from '../services/twoFactorService';

const IndividualLogin = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyPrompt, setVerifyPrompt] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setVerifyPrompt(false);
    dispatch(loginStart());
    try {
      const response = await individualLogin({ email: form.email, password: form.password });

      if (isTwoFactorChallenge(response)) {
        const mode = response.requiresTwoFactorSetup ? 'setup' : 'verify';
        navigate(`/two-factor?mode=${mode}`);
        return;
      }

      if (!response.token) throw new Error('No token received');
      dispatch(loginSuccess({ user: response.user!, token: response.token, agency: response.agency }));
      setTimeout(() => {
        if (response.agency?.slug) {
          navigate(`/agencies/${response.agency.slug}/dashboard`);
        } else {
          navigate('/dashboard');
        }
      }, 100);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      dispatch(loginFailure(msg));
      if (msg.toLowerCase().includes('verify your email')) setVerifyPrompt(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <BrandMark size={36} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.02em' }}>
              Workflow.Estate
            </Typography>
          </Box>
          <Typography component="h1" variant="h5">
            Sign in to your account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            For solo agents on the Elite Individual plan
          </Typography>
          {error && (
            <Alert severity={verifyPrompt ? 'warning' : 'error'} sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/register" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link component={RouterLink} to="/login" variant="body2" color="text.secondary">
                Agency user? Sign in at /login
              </Link>
            </Box>
            {verifyPrompt && (
              <Button href="/verify-email" variant="outlined" sx={{ mt: 2 }}>
                Resend Verification Email
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default IndividualLogin;
